import { createRoot } from "react-dom/client";
import superjson from "superjson";

import { trpc } from "@/lib/trpc";
import { findRoute } from "@base/site/match";
import type { RouteId } from "@base/site/routes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";

import App from "./App";
import { INCLUDE_DRAFTS } from "./app/env";
import { ROUTE_COMPONENTS } from "./app/routeComponents";
import "./index.css";

const queryClient = new QueryClient();

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    console.error("[API Query Error]", event.query.state.error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    console.error("[API Mutation Error]", event.mutation.state.error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

function mount() {
  createRoot(document.getElementById("root")!).render(
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>
  );
}

// Load the current route's code before mounting, so prerendered markup is
// replaced by the real page instead of a Suspense fallback (no layout shift).
// A failed preload still mounts; the route then loads (or errors) as usual.
let match: ReturnType<typeof findRoute> = null;
try {
  match = findRoute(window.location.pathname, INCLUDE_DRAFTS);
} catch {
  // Never let the preload lookup block mounting.
}
const preload = match
  ? ROUTE_COMPONENTS[match.route.id as RouteId].preload()
  : Promise.resolve();
preload.catch(() => undefined).then(mount);
